<template>
  <div class="menu-page">
    <v-card class="mb-4 nd-border" flat>
      <v-card-title class="d-flex justify-space-between">
        {{ webhook.name || 'Webhook' }}
        <router-link to="/webhooks">
          <ib-btn small color="gray200">
            <v-icon left>mdi-keyboard-backspace</v-icon>
            Voltar
          </ib-btn>
        </router-link>
      </v-card-title>

      <v-card-text class="pb-0 mb-2">
        <div class="nd-border px-6 py-5">
          <div class="text-subtitle-1 font-weight-bold text--primary">URL do webhook (endpoint de entrada)</div>

          <div class="d-flex align-center flex-wrap mt-3" style="gap: 12px;">
            <div class="webhook-url-field text-truncate rounded-lg px-4 py-2 body-2 primary--text" :title="webhook.url">{{ webhook.url }}</div>
            <ib-btn color="primary" @click="copyToClipboard">
              <v-icon left>{{ copyUrl ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
              {{ copyUrl ? 'Copiado' : 'Copiar URL' }}
            </ib-btn>
          </div>

          <div class="caption gray600--text mt-3">Envie requisições do tipo <span class="font-weight-bold primary--text">POST</span> com corpo em JSON para este endpoint para acionar o webhook.</div>
        </div>
      </v-card-text>

      <v-card-text>
        <v-row>
          <v-col cols="12" lg="6">
            <ib-text-field v-model="webhook.name" label="Nome" outlined hide-details="auto" />
          </v-col>

          <v-col cols="12" lg="6">
            <ib-autocomplete v-model="webhook.chip" outlined return-object :items="chips" @change="getTemplates" item-text="name" no-data-text="Não há números conectados" item-value="_id" label="Selecione um chip de envio" hide-details="auto" />
          </v-col>

          <v-col cols="12" lg="6" v-if="!webhook.chip?.official">
            <ib-autocomplete v-model="webhook.flow" outlined :items="flows" item-text="name" no-data-text="Não há fluxos criados" item-value="_id" label="Selecione um fluxo" hide-details="auto" />
          </v-col>

          <v-col cols="12" lg="6" v-if="webhook.chip?.official">
            <ib-autocomplete v-model="webhook.template" outlined :items="templates" item-text="name" return-object no-data-text="Não há templates" item-value="name" @change="templateChanged" label="Template de mensagem" hide-details="auto" />
          </v-col>

          <v-col cols="12" lg="6">
            <ib-select v-model="webhook.status" outlined chips small-chips :items="statusOptions" item-text="label" item-value="value" label="Mude o status" hide-details="auto">
              <template #selection="{ item }">
                <ib-chip :tonal="!$isDark" :color="item.color">{{ item.label }}</ib-chip>
              </template>
              <template #item="{ item }">
                <ib-chip :tonal="!$isDark" small :color="item.color">{{ item.label }}</ib-chip>
              </template>
            </ib-select>
          </v-col>

          <v-col cols="12" v-if="webhook.chip?.official && webhook.template">
            <v-expansion-panels v-model="sections.content" flat class="nd-border">
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div>
                    <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                      <div class="text-subtitle-1 font-weight-bold">Conteúdo</div>
                      <ib-chip v-if="templateCategory" small :tonal="!$isDark" color="#1976D2">Categoria: {{ templateCategory }}</ib-chip>
                    </div>
                    <div class="caption gray600--text mt-1">Pré-visualização da mensagem que será enviada pelo template</div>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="template-preview ib-scroll rounded-lg pa-4 body-2">
                    <div v-if="templatePreview.header_media" class="template-preview-media d-flex align-center justify-center rounded mb-2 gray500--text">
                      <v-icon size="28">{{ templatePreview.header_media == 'VIDEO' ? 'mdi-video-outline' : 'mdi-file-document-outline' }}</v-icon>
                      <span class="caption ml-2">{{ templatePreview.header_media == 'VIDEO' ? 'Vídeo do cabeçalho' : 'Documento do cabeçalho' }}</span>
                    </div>

                    <div v-if="templatePreview.header_text" class="pre-wrap font-weight-bold mb-2">{{ templatePreview.header_text }}</div>

                    <div class="pre-wrap">{{ templatePreview.body_text }}</div>

                    <div v-if="templatePreview.footer_text" class="pre-wrap caption gray500--text mt-2">{{ templatePreview.footer_text }}</div>

                    <div v-for="(btn, index) in templatePreview.buttons" :key="`preview_button_${index}`" class="template-preview-button text-center mt-2 pt-2">{{ btn }}</div>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>

          <v-col cols="12">
            <v-expansion-panels v-model="sections.data" flat class="nd-border">
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div>
                    <div class="text-subtitle-1 font-weight-bold">Dados do webhook</div>
                    <div class="caption gray600--text mt-1">São os dados que o webhook vai receber de outros sistemas</div>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="d-flex mb-4">
                    <ib-btn color="primary" outlined class="mr-2" small @click="webhookDataString = JSON.stringify(exampleJson, null, 4);">
                      <v-icon left>mdi-database-arrow-down</v-icon>
                      Exemplo
                    </ib-btn>
                    <ib-btn color="primary" small @click="handleTestEvent()">
                      <v-icon left v-if="!loadingTestEvent">mdi-flask-outline</v-icon>
                      <v-icon left v-else class="mr-1 animate-spin">mdi-loading</v-icon>
                      {{ loadingTestEvent ? 'Aguardando evento...' : 'Receber evento teste' }}
                    </ib-btn>

                    <ib-btn v-show="loadingTestEvent" color="primary" class="ml-2" icon outlined small @click="endSocket()">
                      <v-icon size="21">mdi-stop</v-icon>
                    </ib-btn>
                  </div>

                  <v-textarea v-model="webhookDataString" class="nd-text-field" rows="6" label="Dados recebidos" placeholder="Cole aqui o JSON do Webhook ou aguarde um evento de teste" outlined hide-details="auto" />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>

          <v-col cols="12">
            <v-expansion-panels v-model="sections.fields" flat class="nd-border">
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div>
                    <div class="text-subtitle-1 font-weight-bold">Campos mapeados</div>
                    <div class="caption gray600--text mt-1">Campos que chegam de outro sistema e mapeamento para identificação do contato na plataforma e cadastro de informações no contato</div>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row>
                    <v-col v-for="(field, index) in webhook.fields" :key="index" cols="12" lg="6">
                      <v-card class="d-flex flex-column pa-4 pb-8" style="gap: 20px; ">
                        <v-row class="d-flex">
                          <ib-btn :disabled="index === 0" class="ml-auto" icon small @click="webhook.fields.splice(index, 1)">
                            <v-icon color="red">mdi-close-circle-outline</v-icon>
                          </ib-btn>
                        </v-row>
                        <ib-text-field v-model="field.name" label="Nome do campo" outlined hide-details="auto" :readonly="index === 0" />

                        <ib-autocomplete v-model="field.origin" :items="availablePaths" item-text="text" item-value="value" label="Campos de origem" multiple clearable chips small-chips outlined @change="updateValue(field)" hide-details="auto" :return-object="true" />

                        <ib-text-field :value="field.value" label="Valor exemplo" readonly outlined hide-details="auto" />
                      </v-card>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" class="text-center">
                      <ib-btn color="primary" small @click="webhook.fields.push({ name: '', origin: [], value: '' });">
                        <v-icon left>mdi-plus</v-icon>
                        Novo campo
                      </ib-btn>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>

          <v-col cols="12" v-if="webhook.chip?.official && webhook.template && templateVariables.length > 0">
            <v-expansion-panels v-model="sections.variables" flat class="nd-border">
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div>
                    <div class="text-subtitle-1 font-weight-bold">Variáveis</div>
                    <div class="caption gray600--text mt-1">O template possui variáveis. Mapeie cada variável para um campo que será enviado no webhook</div>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row>
                    <v-col cols="12" lg="6" v-for="(variable, index) in templateVariables" :key="`var_${index}`">
                      <ib-autocomplete v-model="variable.field" :items="webhook.fields.filter(f => f.name)" item-text="name" item-value="name" :label="`Variável {{${variable.position}}} - ${variable.context}`" hide-details="auto" />
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>

          <v-col cols="12" v-if="webhook.chip?.official && webhook.template_payloads?.length">
            <v-expansion-panels v-model="sections.buttons" flat class="nd-border">
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div>
                    <div class="text-subtitle-1 font-weight-bold">Botões</div>
                    <div class="caption gray600--text mt-1">O template selecionado possui botões. Selecione para onde enviar o contato no clique de cada botão</div>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row>
                    <v-col cols="12" v-for="(btn, index) in webhook.template_payloads" :key="`payload_${index}`">
                      <ib-autocomplete v-model="btn.payload" clearable item-text="name" item-value="_id" :items="flows" :label="btn.tip" outlined hide-details="auto" />
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>

          <v-col cols="12" v-if="webhook.chip?.official">
            <v-expansion-panels v-model="sections.conversation" flat class="nd-border">
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div>
                    <div class="text-subtitle-1 font-weight-bold">Criação da conversa</div>
                    <div class="caption gray600--text mt-1">Configure como e quando as conversas serão criadas na plataforma e como será o atendimento após a resposta do contato</div>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-radio-group v-model="webhook.conversation_creation_rule" class="mt-0" row hide-details="auto">
                    <v-radio label="Criar conversa em todos os envios" value="all"></v-radio>
                    <v-radio label="Criar conversa apenas quando o contato responder" value="on_response"></v-radio>
                  </v-radio-group>

                  <v-row v-if="webhook.conversation_creation_rule === 'all'" class="mt-2">
                    <v-col cols="12" lg="6" md="6" sm="12">
                      <ib-select v-model="webhook.redirect" chips mandatory small-chips item-text="name" item-value="id" :items="redirects" label="Tipo de conversa" color="primary" outlined hide-details="auto" />
                    </v-col>

                    <v-col v-if="!webhook.redirect || webhook.redirect == 'ai_chatbot'" cols="12" lg="6" md="6" sm="12">
                      <ib-select v-model="webhook.chatbot" chips mandatory small-chips item-text="name" item-value="_id" :items="chatbots" label="Chatbot" color="primary" outlined hide-details="auto" />
                    </v-col>

                    <v-col v-if="!webhook.redirect || webhook.redirect == 'flow_chatbot'" cols="12" lg="6" md="6" sm="12">
                      <ib-select v-model="webhook.flow" chips mandatory small-chips item-text="name" item-value="_id" :items="flows" label="Fluxo" color="primary" outlined hide-details="auto" />
                    </v-col>

                    <v-col cols="12" lg="6" md="6" sm="12">
                      <ib-select v-model="webhook.department" chips mandatory small-chips item-text="name" item-value="_id" :items="departments" label="Departamento" color="primary" outlined hide-details="auto" />
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>

          <v-col cols="12">
            <div class="d-flex justify-space-between">
              <ib-btn color="gray200" @click="load()">
                <v-icon left>mdi-cancel</v-icon>
                Cancelar
              </ib-btn>
              <ib-btn :loading="loading" color="primary" @click="updateWebook()">
                <v-icon left>mdi-content-save-outline</v-icon>
                Salvar
              </ib-btn>
            </div>
          </v-col>
        </v-row>
      </v-card-text>


    </v-card>
  </div>
</template>

<script>
import Api from "@/lib/Api";
import { hasHeaderImage } from "@/lib/Utils";

let socket; // Variável para a instância do socket
let socket_connection_count = 0; // Controle de tentativas de conexão

export default {
  name: "WebhookSingle",

  data() {
    return {
      webhook: { conversation_creation_rule: 'all' },
      chips: [],
      validChips: [],
      flows: [],
      loading: false,
      dialogCreate: false,
      statusOptions: [
        { value: 'active', label: 'Ativo', color: '#0B9055' },
        { value: 'inactive', label: 'Inativo', color: '#9E9E9E' },
        { value: 'test', label: 'Teste', color: '#1976D2' }
      ],
      redirects: [{ name: 'Chatbot de IA', id: 'ai_chatbot' }, { name: 'Chatbot de Fluxo', id: 'flow_chatbot' }, { name: 'Atendimento humano', id: 'human' }],
      exampleJson: {
        user: {
          id: "12345",
          name: "João Silva",
          phone: {
            ddi: "55",
            number: "18993322541"
          }
        },
        order: {
          total: 99.90,
          date: "2025-07-01",
          status: 'approved'
        }
      },
      availablePaths: [],
      chatbots: [],
      departments: [],
      templates: [],
      webhookDataString: '',
      copyUrl: false,
      loadingTestEvent: false,
      eventTimeout: null,
      debounceTimer: null,
      templateVariables: [],
      sections: { content: 0, data: 0, fields: 0, variables: 0, buttons: 0, conversation: 0 }
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },

    webhookId() {
      return this.$route.params.webhookId
    },

    templatePreview() {
      const components = this.webhook.template?.components || [];
      const header = components.find(c => c.type === 'HEADER');
      const body = components.find(c => c.type === 'BODY');
      const footer = components.find(c => c.type === 'FOOTER');
      const buttons = components.find(c => c.type === 'BUTTONS')?.buttons || [];
      const isMediaHeader = ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header?.format);

      return {
        header_media: isMediaHeader ? header.format : null,
        header_text: isMediaHeader ? '' : header?.text || '',
        body_text: body?.text || '',
        footer_text: footer?.text || '',
        buttons: buttons.map(b => {
          if (b.type === 'URL') return `🔗 ${b.text}`;
          if (b.type === 'PHONE_NUMBER') return `📞 ${b.text}`;
          return b.text;
        })
      };
    },

    templateCategory() {
      const categories = { UTILITY: 'Utilidade', MARKETING: 'Marketing', AUTHENTICATION: 'Autenticação' };
      const category = this.webhook.template?.category;
      return categories[category] || category || '';
    }
  },

  watch: {
    webhookDataString(newValue) {
      this.processWebhookJson(newValue);
    }
  },

  async mounted() {
    if (!this.currentUser?.access_areas?.includes('api')) {
      this.$toast("error", "Permissões insuficientes.");
      this.$router.replace({ name: 'Atendimento' });
    }

    this.load();
  },

  async beforeDestroy() {
    if (socket) {
      this.endSocket();
    }
  },

  methods: {
    extractJsonPaths(obj, prefix = '') {
      return Object.entries(obj).reduce((acc, [key, val]) => {
        const path = prefix ? `${prefix}.${key}` : key;

        if (val !== null && typeof val === 'object') {
          if (Array.isArray(val)) {
            // percorre o array
            val.forEach((item, index) => {
              if (item !== null && typeof item === 'object') {
                acc = acc.concat(this.extractJsonPaths(item, `${path}[${index}]`));
              } else {
                acc = acc.concat({ path: `${path}[${index}]`, value: String(item) });
              }
            });
            return acc;
          } else {
            // objeto normal
            return acc.concat(this.extractJsonPaths(val, path));
          }
        } else {
          return acc.concat({ path, value: String(val) });
        }
      }, []);
    },

    updateValue(field) {
      const pathsList = this.extractJsonPaths(JSON.parse(this.webhookDataString));
      const lookup = pathsList.reduce((m, item) => { m[item.path] = item.value; return m; }, {});
      field.value = field.origin.map(p => lookup[p.value] || '').filter(v => v !== '').join('');
    },

    processWebhookJson(jsonString) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        try {
          const parsed = JSON.parse(jsonString);
          this.webhookDataString = jsonString;
          this.availablePaths = this.extractJsonPaths(parsed).map(item => ({
            text: `${item.path}: ${item.value}`,
            value: item.path
          }));
          this.webhook.fields.forEach(field => { this.updateValue(field) });
        } catch (e) {
          this.availablePaths = [];
          this.webhook.fields.forEach(field => { field.value = null });
          this.$toast('error', 'O conteúdo fornecido não é um JSON válido.');
        }
      }, 1200);
    },

    templateChanged(template) {
      const buttons = template.components.find(c => c.type === 'BUTTONS');
      this.webhook.template_payloads = [];

      if (buttons && buttons.buttons) {
        buttons.buttons.forEach(b => {
          if (b.type === "QUICK_REPLY") this.webhook.template_payloads.push({ type: 'button', button: b.text, tip: `Se o contato clicar em ${b.text}`, payload: '' });
        });
      }

      this.extractTemplateVariables(template);

      this.$forceUpdate();
    },

    extractTemplateVariables(template) {
      const variables = [];

      // Processa componente HEADER
      const headerComponent = template.components?.find(c => c.type === 'HEADER');
      if (headerComponent && headerComponent.text) {
        const matches = headerComponent.text.match(/\{\{\d+\}\}/g) || [];
        matches.forEach(match => {
          const pos = parseInt(match.replace(/[{}]/g, ''));
          const existingVar = this.webhook.template_variables?.find(v => v.position === pos && v.component === 'HEADER');
          variables.push({ position: pos, component: 'HEADER', context: 'Cabeçalho da mensagem', field: existingVar?.field || null });
        });
      }

      // Processa componente BODY
      const bodyComponent = template.components?.find(c => c.type === 'BODY');
      if (bodyComponent && bodyComponent.text) {
        const matches = bodyComponent.text.match(/\{\{\d+\}\}/g) || [];
        matches.forEach(match => {
          const pos = parseInt(match.replace(/[{}]/g, ''));
          const existingVar = this.webhook.template_variables?.find(v => v.position === pos && v.component === 'BODY');
          variables.push({ position: pos, component: 'BODY', context: 'Corpo da mensagem', field: existingVar?.field || null });
        });
      }

      this.templateVariables = variables.sort((a, b) => a.position - b.position);
      this.webhook.template_variables = this.templateVariables;
    },

    async load() {
      this.loading = true;
      const webhook = await Api.getWebhookById(this.webhookId);

      if (!webhook.error && webhook.message) {
        this.webhook = webhook.message;

        if (this.webhook.data || this.webhook.field) {
          this.webhookDataString = JSON.stringify(this.webhook.data, null, 4)
          this.availablePaths = this.extractJsonPaths(this.webhook.data).map(item => ({ text: `${item.path}: ${item.value}`, value: item.path }));
        }

        const existingTelefoneField = this.webhook.fields?.find(f => f.name === 'telefone');
        this.webhook.fields = [
          {
            name: 'telefone',
            origin: existingTelefoneField?.origin || [],
            value: existingTelefoneField?.value || null
          },
          ...(this.webhook.fields?.filter(f => f.name !== 'telefone') || [])
        ]

        const chatbots = await Api.getChatbotsByUser();
        if (!chatbots.error && chatbots.message) this.chatbots = chatbots.message;

        const departments = await Api.getDepartmentsByUser();
        if (!departments.error && departments.message) this.departments = departments.message;

        if (this.webhook.chip) await this.getTemplates();
        
        if (this.webhook.template) this.extractTemplateVariables(this.webhook.template);
      }

      const chips = await Api.getChipNumbersByUser();

      if (!chips.error && chips.message) {
        this.chips = chips.message;
        this.validChips = [];
        let hasConnection = false;

        chips.message.forEach(chip => {
          if (chip.status == 'open') {
            this.validChips.push(chip.name);
            hasConnection = true;
          }
        });

        if (!hasConnection) this.showAlert = true;
      }

      const flows = await Api.getFlowsByUser();
      if (!flows.error && flows.message) {
        this.flows = flows.message;
      }

      this.loading = false;
    },

    async getTemplates() {
      if (this.webhook && this.webhook.chip?.waba) {
        const templates = await Api.getTemplateMessages(this.webhook.chip?.waba);
        if (!templates.error && templates.message) this.templates = templates.message.filter(t => t.status === 'APPROVED' && !hasHeaderImage(t.components));
      }
    },

    async updateWebook() {
      if (this.webhook.fields[0]?.origin.length === 0) return this.$toast('error', 'O campo telefone deve ser mapeado.');

      if (this.templateVariables.length > 0) {
        const unmappedVars = this.templateVariables.filter(v => !v.field);
        if (unmappedVars.length > 0) {
          return this.$toast('error', `Todas as variáveis do template devem ser mapeadas. Faltam: ${unmappedVars.map(v => `{{${v.position}}}`).join(', ')}`);
        }
      }

      try {
        this.webhook.data = JSON.parse(this.webhookDataString);
      } catch (error) {
        return this.$toast('error', 'O conteúdo recebido do webhook não é um JSON válido.');
      }

      this.webhook.template_variables = this.templateVariables;

      this.loading = true;
      const resp = await Api.createOrUpdateWebhook(this.webhook);
      this.loading = false;

      if (resp.error && resp.message) {
        return
      }

      this.$toast("success", "Webhook atualizado!");
    },

    copyToClipboard() {
      navigator.clipboard.writeText(this.webhook.url);
      this.copyUrl = true;
      setTimeout(() => {
        this.copyUrl = false;
      }, 1500);
    },

    handleTestEvent() {
      if (this.webhook.status !== 'test') return this.$toast("error", "Webhook deve estar no status teste.");

      this.startSocket();
      this.loadingTestEvent = true;

      this.eventTimeout = setTimeout(() => {
        this.endSocket();
        this.$toast("warning", "Tempo esgotado. Nenhum evento recebido.");
      }, 60000);
    },

    startSocket() {
      if (socket_connection_count >= 3) {
        this.$toast("error", "Erro na conexão com o socket. Atualize a página.");
        return;
      }

      if (!this.$store.state.SOCKET_IO_URL) {
        socket_connection_count++;
        setTimeout(() => { this.startSocket(); }, 500 + socket_connection_count * 2000);
        return;
      }

      socket_connection_count = 0;
      socket = this.webhook.chip.official ? this.$io(this.$store.state.OFFICIAL_SOCKET_IO_URL, { path: '/sock/' }) : this.$io(this.$store.state.SOCKET_IO_URL, { path: '/sock/' });

      socket.on("connect", () => {
        socket.emit("join-room", `webhook_${this.webhook._id}`);
      });

      socket.on("receive-message", data => {
        this.webhook.data = data.payload;
        this.webhookDataString = JSON.stringify(data.payload, null, 4);
        this.processWebhookJson(this.webhookDataString)
        this.endSocket();
      });
    },

    endSocket() {
      this.loadingTestEvent = false;
      clearTimeout(this.eventTimeout);

      socket.disconnect();
      socket = null;
    }
  }
};
</script>

<style scoped>
.animate-spin {
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.webhook-url-field {
  flex: 1 1 280px;
  font-family: 'Roboto Mono', Consolas, monospace;
}

.webhook-url-field,
.template-preview {
  background-color: var(--v-gray50-base);
  border: 1px solid var(--v-gray200-base);
}

.v-expansion-panels.nd-border > .v-expansion-panel {
  border-radius: inherit !important;
}

.template-preview {
  max-height: 420px;
  overflow-y: auto;
  overflow-wrap: anywhere;
}

.template-preview-media {
  height: 80px;
  border: 1px dashed var(--v-gray400-base);
}

.pre-wrap {
  white-space: pre-wrap;
}

.template-preview-button {
  border-top: 1px solid var(--v-gray200-base);
  color: #0a7cff;
}

.theme--dark .template-preview-button {
  color: #4da3ff;
}
</style>